import { Component, OnInit, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteActions } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { SiteState, VarietyProperties } from '../shared/models';
import {
  GetVarietyProperties,
  OpenCreateVarietyPropertyDialog,
  GetCropVarieties,
  GetProductionPurposeCategories,
  OpenEditVarietyPropertyDialog,
  DeleteVarietyProperties,
} from '../store/actions';
import { selectVarietyProperties } from '../store/selectors';

enum VarietyPropertyAction {
  edit = 'edit',
  remove = 'remove',
}

@Component({
  selector: 'app-variety-properties',
  templateUrl: './variety-properties.component.html',
  styleUrls: ['./variety-properties.component.scss'],
  standalone: false,
})
export class VarietyPropertiesComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  title = this.locale.translate('varietyProperties.title');
  displayedColumns: string[] = [
    'cropVarietyName',
    'productionPurposeCategoryName',
    'foliageMax',
    'day50Percent',
    'nEfficiency',
    'nNorm',
    'nMax',
    'kMin',
    'kMax',
    'giftcorrection',
    'plantHa',
    'maturity',
    'mgMin',
    'mgMax',
    'actions',
  ];

  varietyPropertyActions = [
    {
      icon: 'edit',
      viewValue: this.locale.translate('edit'),
      key: VarietyPropertyAction.edit,
    },
    {
      icon: 'delete',
      viewValue: this.locale.translate('remove'),
      key: VarietyPropertyAction.remove,
    },
  ];

  dataSource = new MatTableDataSource<VarietyProperties>();
  dataAvailable = false;
  isMobile$ = this.breakpoint.ltLg$;
  private readonly subscription = new Subscription();

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: this.title }));
    this.store.dispatch(new GetVarietyProperties());
    this.store.dispatch(new GetCropVarieties());
    this.store.dispatch(new GetProductionPurposeCategories());
    const sub = this.store
      .pipe(select(selectVarietyProperties))
      .subscribe((items: VarietyProperties[] | undefined) => {
        this.dataSource.data = items ?? [];
        this.dataAvailable = (items ?? []).length > 0;
        this.cdr.detectChanges();
      });

    this.subscription.add(sub);

    this.updateData();
  }

  updateData() {
    this.store.dispatch(new GetVarietyProperties());
  }

  onPage() {
    this.updateData();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  createVarietyProperty() {
    this.store.dispatch(new OpenCreateVarietyPropertyDialog());
  }

  varietyPropertyAction(key: string, id: number) {
    switch (key) {
      case VarietyPropertyAction.edit:
        this.editVarietyProperty(id);
        break;
      case VarietyPropertyAction.remove:
        this.removeVarietyProperty(id);
        break;
    }
  }

  editVarietyProperty(id: number) {
    const varietyProperty = this.dataSource.data.find((v: VarietyProperties) => v.id === id);
    if (!varietyProperty) return;
    this.store.dispatch(new OpenEditVarietyPropertyDialog({ varietyProperty }));
  }

  removeVarietyProperty(id: number) {
    this.store.dispatch(new DeleteVarietyProperties(id));
  }
}
